import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useState, type JSX } from 'react';
import orderPlacedImage from '@/assets/images/orderPlaced.png';
import ForwardArrow from '@/assets/svg/static/arrow.svg';
import ExchangeIcon from '@/assets/svg/static/exchangeBox.svg';
import FeesIcon from '@/assets/svg/static/fees.svg';
import { EVENTS, PROPERTIES, trackEvent } from '@/services/posthog';
import {
  fetchGuestOrderDetail,
  fetchProductDataById,
} from '@/services/slaydAPI';
import useDownloadAppSheetStore from '@/stores/DownloadAppSheetStore';
import useLayoutStore from '@/stores/useLayoutStore';
import { PaymentMode, type GuestOrder } from '@/types/Order.types';
import { updatedImageUrl } from '@/utils/formatting';
import {
  CONTINUE_SHOPPING_TEXT,
  DELIVERY_ESTIMATE_TEXT,
  DELIVERY_FEE,
  DELIVERY_PARTNER_TEXT,
  FALLBACK_BUTTON_TEXT,
  FALLBACK_SUBTITLE,
  FALLBACK_TITLE,
  LABEL_CANCEL_RETURN,
  LABEL_DELIVERY_FEES,
  LABEL_DELIVERY_FEES_STATIC,
  LABEL_DISCOUNT,
  LABEL_FINAL_PRICE,
  LABEL_PRODUCT_PRICE,
  LABEL_TRACK_ORDER,
  ORDER_PLACED_TITLE,
  RETURN_POLICY_TEXT,
  SECTION_DELIVERY_ADDRESS,
  SECTION_MODE_OF_PAYMENT,
  SECTION_PAYMENT_DETAILS,
  ZERO_PRICE_LABEL,
} from './OrderPlacedPage.constants';
import styles from './OrderPlacedPage.module.css';

export default function OrderPlacedPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const guestOrderId = searchParams.get('guestOrderId');
  const navigate = useNavigate();

  const [order, setOrder] = useState<GuestOrder | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const setNavbarVisibility = useLayoutStore(
    (state) => state.setNavbarVisibility
  );
  const { openSheet: openDownloadSheet } = useDownloadAppSheetStore();

  // Hide bottom navbar when page is loaded, restore when navigating away
  useEffect(() => {
    setNavbarVisibility(false);
    return () => {
      setNavbarVisibility(true);
    };
  }, [setNavbarVisibility]);

  const loadOrder = useCallback(async (): Promise<void> => {
    if (!guestOrderId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const guestOrder = await fetchGuestOrderDetail(guestOrderId);

      // Enrich the items in a single pass/loop
      await Promise.all(
        guestOrder.items.map(async (item) => {
          try {
            const productData = await fetchProductDataById(
              item.productId.toString()
            );
            item.imageUrl = productData?.imageUrl || '';
            item.brandName =
              productData?.brand || productData?.brandDetails?.brandName || '';
          } catch (err) {
            console.error(
              `Failed to fetch product metadata for ID ${item.productId}`,
              err
            );
          }
        })
      );

      setOrder(guestOrder);
    } catch (err) {
      console.error('[OrderPlacedPage] loadOrder failed:', err);
    } finally {
      setLoading(false);
    }
  }, [guestOrderId]);

  useEffect(() => {
    if (!guestOrderId) {
      navigate('/');
      return;
    }
    loadOrder();
  }, [guestOrderId, navigate, loadOrder]);

  // Track page view once order details are successfully fetched
  useEffect(() => {
    if (order) {
      const productIds = (order.items || [])
        .map((i) => i.productId.toString())
        .join(',');
      const productPrice = (order.items || []).reduce((sum, item) => {
        const price = parseFloat(
          item.dealPriceAtPurchase || item.mrpAtPurchase
        );
        return sum + price * item.quantity;
      }, 0);
      const finalPrice = order.razorpayAmount
        ? parseFloat(order.razorpayAmount)
        : productPrice;

      trackEvent(EVENTS.ORDER_PLACED_VIEW, {
        [PROPERTIES.GUEST_ORDER_ID]: order.guestOrderId,
        [PROPERTIES.STATUS]: order.status,
        [PROPERTIES.AMOUNT]: finalPrice,
        [PROPERTIES.ITEM_COUNT]: order.items?.length || 0,
        [PROPERTIES.PAYMENT_MODE]: order.paymentMode || '',
        [PROPERTIES.PRODUCT_IDS]: productIds,
      });
    }
  }, [order]);

  const handleContinueShopping = (): void => {
    trackEvent(EVENTS.ORDER_PLACED_CONTINUE_CLICK, {
      [PROPERTIES.GUEST_ORDER_ID]: guestOrderId || '',
    });
    navigate('/');
  };

  const handleActionClick = (actionName: string): void => {
    trackEvent(EVENTS.ORDER_PLACED_ACTION_CLICK, {
      [PROPERTIES.ACTION]: actionName,
      [PROPERTIES.GUEST_ORDER_ID]: guestOrderId || '',
    });
    // Open app download sheet as post-purchase actions (tracking/cancellations) are app-only
    openDownloadSheet('personalize');
  };

  if (loading) {
    return (
      <div className={styles.parentContainer}>
        <div className={styles.container}>
          {/* Skeleton Hero */}
          <div className={styles.heroSkeleton}>
            <div className={`${styles.skeleton} ${styles.skeletonCircle}`} />
            <div
              className={`${styles.skeleton} ${styles.skeletonText}`}
              style={{ width: '40%', height: '24px', marginTop: '16px' }}
            />
          </div>
          {/* Skeleton Section */}
          <div className={styles.sectionSkeleton}>
            <div
              className={`${styles.skeleton} ${styles.skeletonText}`}
              style={{ width: '30%', height: '16px' }}
            />
            <div
              className={`${styles.skeleton} ${styles.skeletonText}`}
              style={{ width: '90%', height: '14px', marginTop: '12px' }}
            />
            <div
              className={`${styles.skeleton} ${styles.skeletonText}`}
              style={{ width: '70%', height: '14px', marginTop: '8px' }}
            />
          </div>
          <div className={styles.divider} />
          {/* Skeleton Product Row */}
          <div className={styles.productSkeleton}>
            <div className={`${styles.skeleton} ${styles.skeletonImage}`} />
            <div className={styles.productSkeletonRight}>
              <div
                className={`${styles.skeleton} ${styles.skeletonText}`}
                style={{ width: '40%', height: '14px' }}
              />
              <div
                className={`${styles.skeleton} ${styles.skeletonText}`}
                style={{ width: '80%', height: '14px', marginTop: '8px' }}
              />
              <div
                className={`${styles.skeleton} ${styles.skeletonText}`}
                style={{ width: '60%', height: '14px', marginTop: '8px' }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className={styles.parentContainer}>
        <div className={styles.container}>
          <div className={styles.fallbackContainer}>
            <h3 className={styles.fallbackTitle}>{FALLBACK_TITLE}</h3>
            <p className={styles.fallbackSubtitle}>{FALLBACK_SUBTITLE}</p>
            <button
              className={styles.fallbackButton}
              onClick={() => handleActionClick('fallback_my_orders')}
            >
              {FALLBACK_BUTTON_TEXT}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate pricing breakdown
  const productPrice = order.items.reduce((sum, item) => {
    const price = parseFloat(item.dealPriceAtPurchase || item.mrpAtPurchase);
    return sum + price * item.quantity;
  }, 0);
  const shippingFeeVal = order.shippingFee ? parseFloat(order.shippingFee) : 0;
  const codFeeVal = order.codFee ? parseFloat(order.codFee) : 0;
  const discountVal = order.promotionDiscount
    ? parseFloat(order.promotionDiscount)
    : 0;
  const finalPriceVal = order.razorpayAmount
    ? parseFloat(order.razorpayAmount)
    : productPrice + shippingFeeVal + codFeeVal - discountVal;

  const products = order.items;

  return (
    <div className={styles.parentContainer}>
      <div className={styles.container}>
        <div className={styles.scrollContent}>
          {/* Hero Header */}
          <div className={styles.hero}>
            <img
              src={orderPlacedImage}
              alt={ORDER_PLACED_TITLE}
              className={styles.heroImage}
            />
            <h1 className={styles.heroTitle}>{ORDER_PLACED_TITLE}</h1>
          </div>

          {/* Delivery Address Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>{SECTION_DELIVERY_ADDRESS}</h2>
            <p className={styles.addressText}>
              {order.shippingAddress
                ? [
                    order.shippingAddress.line1,
                    order.shippingAddress.line2,
                    order.shippingAddress.city,
                    order.shippingAddress.state &&
                      `${order.shippingAddress.state}${
                        order.shippingAddress.zipcode
                          ? ` - ${order.shippingAddress.zipcode}`
                          : ''
                      }`,
                  ]
                    .filter(Boolean)
                    .join(', ')
                : '—'}
            </p>
            <div className={styles.deliveryIconRow}>
              <img
                src={FeesIcon}
                alt="Estimate"
                className={styles.deliveryIcon}
              />
              <p className={styles.deliveryIconText}>
                {DELIVERY_ESTIMATE_TEXT}
              </p>
            </div>
            <div className={styles.deliveryIconRow}>
              <img
                src={ExchangeIcon}
                alt="Return Policy"
                className={styles.deliveryIcon}
              />
              <p className={styles.deliveryIconText}>{RETURN_POLICY_TEXT}</p>
            </div>
          </div>

          {/* Delivery Partner Notice */}
          <div className={styles.deliveryPartnerNoticeContainer}>
            <div className={styles.deliveryPartnerNotice}>
              <p className={styles.deliveryPartnerNoticeText}>
                {DELIVERY_PARTNER_TEXT}
              </p>
            </div>
          </div>

          <div className={styles.divider} />

          {/* Product Items */}
          {products.map((item) => (
            <div key={item.productId} className={styles.productRow}>
              <div className={styles.productImageWrapper}>
                <img
                  src={updatedImageUrl(item.imageUrl || '')}
                  alt={item.productName}
                  className={styles.productImage}
                />
              </div>
              <div className={styles.productRowRight}>
                <div className={styles.productInfo}>
                  <h3 className={styles.productBrand}>
                    {item.brandName || ''}
                  </h3>
                  <p className={styles.productName}>{item.productName}</p>
                  <div className={styles.productPriceRow}>
                    <span className={styles.productPriceSale}>
                      {`₹${parseFloat(item.dealPriceAtPurchase || item.mrpAtPurchase).toFixed(2)}`}
                    </span>
                    <span className={styles.productPriceOriginal}>
                      {`₹${parseFloat(item.mrpAtPurchase).toFixed(2)}`}
                    </span>
                  </div>
                  <p className={styles.productMeta}>
                    {'Size '}
                    <span className={styles.productMetaBold}>
                      {item.size ?? '—'}
                    </span>
                    {'   Qty '}
                    <span className={styles.productMetaBold}>
                      {item.quantity}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          ))}

          <div className={styles.divider} />

          {/* Payment Details Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>{SECTION_PAYMENT_DETAILS}</h2>
            <div className={styles.paymentRow}>
              <span className={styles.paymentRowKey}>
                {LABEL_PRODUCT_PRICE}
              </span>
              <span className={styles.paymentRowValue}>
                {`₹${productPrice.toFixed(2)}`}
              </span>
            </div>
            {order.paymentMode === PaymentMode.COD && (
              <div className={styles.paymentRow}>
                <span className={styles.paymentRowKey}>
                  {LABEL_DELIVERY_FEES}
                </span>
                <span className={styles.paymentRowValue}>
                  {`₹${(shippingFeeVal + codFeeVal).toFixed(2)}`}
                </span>
              </div>
            )}
            <div className={styles.paymentRow}>
              <span className={styles.paymentRowKey}>
                {LABEL_DELIVERY_FEES_STATIC}
              </span>
              <span className={styles.paymentRowValue}>
                <span className={styles.strikethrough}>
                  {`₹${DELIVERY_FEE.toFixed(2)}`}
                </span>
                {` ${ZERO_PRICE_LABEL}`}
              </span>
            </div>
            <div className={styles.paymentRow}>
              <span className={styles.paymentRowKey}>{LABEL_DISCOUNT}</span>
              <span className={styles.paymentRowValue}>
                {discountVal === 0
                  ? `₹${discountVal.toFixed(2)}`
                  : `-₹${discountVal.toFixed(2)}`}
              </span>
            </div>
            <div className={styles.paymentRow}>
              <span className={styles.paymentRowKeyBold}>
                {LABEL_FINAL_PRICE}
              </span>
              <span className={styles.paymentRowValueFinal}>
                {`₹${finalPriceVal.toFixed(2)}`}
              </span>
            </div>
          </div>

          <div className={styles.divider} />

          {/* Mode of Payment Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>{SECTION_MODE_OF_PAYMENT}</h2>
            <p className={styles.paymentRowKey}>
              {order.paymentMode?.toUpperCase() || '—'}
            </p>
          </div>

          <div className={styles.divider} />

          {/* Action Row: Track Order */}
          {order.status === 'CONFIRMED' && (
            <button
              className={styles.actionRow}
              onClick={() => handleActionClick('track_order')}
            >
              <span className={styles.actionRowText}>{LABEL_TRACK_ORDER}</span>
              <img src={ForwardArrow} alt="Go" className={styles.actionArrow} />
            </button>
          )}

          {/* Action Row: Cancel / Return */}
          {order.status === 'CONFIRMED' && (
            <button
              className={styles.actionRow}
              onClick={() => handleActionClick('cancel_return')}
            >
              <span className={styles.actionRowText}>
                {LABEL_CANCEL_RETURN}
              </span>
              <img src={ForwardArrow} alt="Go" className={styles.actionArrow} />
            </button>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button
            className={styles.continueButton}
            onClick={handleContinueShopping}
          >
            {CONTINUE_SHOPPING_TEXT}
          </button>
        </div>
      </div>
    </div>
  );
}
